import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import type { PaginationState } from "./usePagination";

export type VendorType = "subcontractor" | "material_supplier" | "equipment_rental" | "general";
export type PaymentTerms = "due_on_receipt" | "net_15" | "net_30" | "net_45" | "net_60";

export interface Vendor {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  vendor_type: string | null;
  vendor_types: string[] | null;
  vendor_number: number | null;
  contact_name: string | null;
  billing_address: string | null;
  billing_city: string | null;
  billing_state: string | null;
  billing_zip: string | null;
  notes: string | null;
  license_number: string | null;
  license_state: string | null;
  license_type: string | null;
  license_expiry: string | null;
  insurance_expiry: string | null;
  insurance_carrier: string | null;
  w9_on_file: boolean;
  ein: string | null;
  is_1099_eligible: boolean;
  lien_waiver_required: boolean;
  default_retainage_pct: number;
  account_number: string | null;
  credit_limit: number | null;
  payment_terms: string | null;
  payment_terms_custom_days: number | null;
  compliance_status: string | null;
  compliance_approved_by: string | null;
  compliance_approved_at: string | null;
  w9_received_date: string | null;
  onboarding_notes: string | null;
  qb_vendor_id: string | null;
  qb_sync_status: string | null;
  qb_synced_at: string | null;
  is_deleted: boolean;
  created_at: string;
  company_id: string;
}

export const useVendors = (pagination?: PaginationState, search?: string) => {
  const { user } = useAuth();
  return useQuery({
    queryKey: ["vendors", pagination?.page, pagination?.pageSize, search],
    queryFn: async () => {
      if (pagination) {
        let countQuery = supabase
          .from("vendors")
          .select("id", { count: "exact", head: true })
          .eq("is_deleted", false);
        if (search) countQuery = countQuery.ilike("name", `%${search}%`);
        const { count } = await countQuery;
        pagination.setTotalCount(count ?? 0);
      }

      let query = (supabase.from("vendors") as any)
        .select("*")
        .eq("is_deleted", false)
        .order("name");

      if (search) query = query.ilike("name", `%${search}%`);

      if (pagination) {
        query = query.range(pagination.from, pagination.to);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as unknown as Vendor[];
    },
    enabled: !!user,
  });
};

export interface CreateVendorInput {
  name: string;
  vendor_types?: VendorType[];
  contact_name?: string;
  email?: string;
  phone?: string;
  billing_address?: string;
  billing_city?: string;
  billing_state?: string;
  billing_zip?: string;
  notes?: string;
  payment_terms?: PaymentTerms;
  license_number?: string;
  license_state?: string;
  license_expiry?: string;
  insurance_carrier?: string;
  insurance_expiry?: string;
  w9_on_file?: boolean;
  ein?: string;
  is_1099_eligible?: boolean;
  lien_waiver_required?: boolean;
  default_retainage_pct?: number;
  account_number?: string;
  credit_limit?: number;
}

export const useCreateVendor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateVendorInput) => {
      const { data: companyId, error: cidErr } = await supabase.rpc("my_company_id");
      if (cidErr) throw cidErr;

      const { data, error } = await supabase
        .from("vendors")
        .insert({
          company_id: companyId,
          name: input.name,
          vendor_types: input.vendor_types || ["subcontractor"],
          vendor_type: (input.vendor_types || ["subcontractor"])[0],
          contact_name: input.contact_name || null,
          email: input.email || null,
          phone: input.phone || null,
          billing_address: input.billing_address || null,
          billing_city: input.billing_city || null,
          billing_state: input.billing_state || null,
          billing_zip: input.billing_zip || null,
          notes: input.notes || null,
          payment_terms: input.payment_terms || "net_30",
          payment_terms_custom_days: (input as any).payment_terms_custom_days ?? null,
          license_number: input.license_number || null,
          license_state: input.license_state || null,
          license_expiry: input.license_expiry || null,
          insurance_carrier: input.insurance_carrier || null,
          insurance_expiry: input.insurance_expiry || null,
          w9_on_file: input.w9_on_file ?? false,
          w9_received_date: (input as any).w9_received_date || null,
          ein: input.ein || null,
          is_1099_eligible: input.is_1099_eligible ?? false,
          lien_waiver_required: input.lien_waiver_required ?? true,
          default_retainage_pct: input.default_retainage_pct ?? 10,
          account_number: input.account_number || null,
          credit_limit: input.credit_limit ?? null,
          compliance_status: (input as any).compliance_status || "pending",
          onboarding_notes: (input as any).onboarding_notes || null,
          onboarding_track: (input as any).onboarding_track || "subcontractor",
          onboarding_status: (input as any).onboarding_status || "not_invited",
          qb_sync_status: "pending",
        } as any)
        .select("id, name")
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
};

export const useUpdateVendor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...input }: CreateVendorInput & { id: string }) => {
      const { error } = await supabase
        .from("vendors")
        .update({
          name: input.name,
          vendor_types: input.vendor_types || ["subcontractor"],
          vendor_type: (input.vendor_types || ["subcontractor"])[0],
          contact_name: input.contact_name || null,
          email: input.email || null,
          phone: input.phone || null,
          billing_address: input.billing_address || null,
          billing_city: input.billing_city || null,
          billing_state: input.billing_state || null,
          billing_zip: input.billing_zip || null,
          notes: input.notes || null,
          payment_terms: input.payment_terms || "net_30",
          license_number: input.license_number || null,
          license_state: input.license_state || null,
          license_expiry: input.license_expiry || null,
          insurance_carrier: input.insurance_carrier || null,
          insurance_expiry: input.insurance_expiry || null,
          w9_on_file: input.w9_on_file ?? false,
          ein: input.ein || null,
          is_1099_eligible: input.is_1099_eligible ?? false,
          lien_waiver_required: input.lien_waiver_required ?? true,
          default_retainage_pct: input.default_retainage_pct ?? 10,
          account_number: input.account_number || null,
          credit_limit: input.credit_limit ?? null,
        } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
};

export const useSoftDeleteVendor = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("vendors")
        .update({ is_deleted: true } as any)
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["vendors"] });
    },
  });
};

export function getComplianceStatus(vendor: Vendor) {
  const now = new Date();
  const soon = new Date();
  soon.setDate(soon.getDate() + 30);

  const issues: { level: "red" | "yellow"; label: string }[] = [];
  const types = vendor.vendor_types || [vendor.vendor_type || "general"];
  const isSub = types.includes("subcontractor");

  if (isSub) {
    if (!vendor.w9_on_file) issues.push({ level: "red", label: "No W9" });
    if (vendor.insurance_expiry) {
      const exp = new Date(vendor.insurance_expiry);
      if (exp < now) issues.push({ level: "red", label: "Insurance expired" });
      else if (exp < soon) issues.push({ level: "yellow", label: "Insurance expiring" });
    } else {
      issues.push({ level: "red", label: "No COI" });
    }
    if (vendor.license_expiry) {
      const exp = new Date(vendor.license_expiry);
      if (exp < now) issues.push({ level: "red", label: "License expired" });
      else if (exp < soon) issues.push({ level: "yellow", label: "License expiring" });
    }
  }

  const overallLevel = issues.some((i) => i.level === "red")
    ? "red"
    : issues.some((i) => i.level === "yellow")
    ? "yellow"
    : "green";

  return { level: overallLevel as "green" | "yellow" | "red", issues };
}
